import { useEffect, useRef } from "react";

import { isValidDate } from "@shared";
import { addDays, addMinutes, differenceInCalendarDays, getHours, getMinutes, set } from "date-fns";
import { minutesInDay, minutesInHour } from "date-fns/constants";
import { FormikProps, FormikValues } from "formik";

import { DefaultAppointmentDurationMinutes } from "@/common/components/appointments/shared/appointmentConstants";

interface Options<T extends FormikValues> {
  /**
   * What an empty end takes: the start plus this many minutes. A date-only pair passes 0, since a
   * duration added to a day at midnight would leave a stray time on the end day.
   */
  emptyEndDurationMinutes?: number;
  /**
   * For a pair whose end time sits in a field of its own (Uhrzeit bis): the end-day field (Datum bis)
   * that takes the days the end time carries past midnight, so both dialogs keep the duration alike.
   */
  endDayField?: keyof T & string;
}

/**
 * Moves the end of an appointment (Termin) by as many days and minutes as its start moved, so the
 * duration entered so far is kept — whether it is the default or one the staff member set by editing
 * the end. One handler serves the start date and the start time picker alike: each picker changes only
 * its own half of the value, so the other half's distance is zero. An empty end takes the start plus
 * the empty-end duration instead. The returned handler belongs on the start pickers' `onValueChange`.
 */
export function useEndFollowingStart<T extends FormikValues>(
  formik: FormikProps<T>,
  startField: keyof T & string,
  endField: keyof T & string,
  { emptyEndDurationMinutes = DefaultAppointmentDurationMinutes, endDayField }: Options<T> = {}
): (nextStart: Date | null) => void {
  // The start the end was last measured against. The form field holds it until the picker's write
  // renders, but keyboard editing empties the field between keystrokes — so on the keystroke that
  // completes a retyped value there is nothing left in it to measure from, and the ref steps in.
  const lastValidStart = useRef<Date | null>(formik.values[startField]);

  // Other code writes the start too — the all-day switch moves it to 08:00 — and the ref must not fall
  // behind such a write, or the next keyboard edit measures the end's shift from a start no longer shown.
  const start = formik.values[startField];
  useEffect(() => {
    if (isValidDate(start)) {
      lastValidStart.current = start;
    }
  }, [start]);

  const carryIntoEndDay = (days: number) => {
    const endDay = endDayField ? formik.values[endDayField] : null;
    if (endDayField && days !== 0 && isValidDate(endDay)) {
      void formik.setFieldValue(endDayField, addDays(endDay, days));
    }
  };

  return (nextStart: Date | null) => {
    if (!isValidDate(nextStart)) {
      return;
    }

    const previousStart = isValidDate(start) ? start : lastValidStart.current;
    const currentEnd = formik.values[endField];
    lastValidStart.current = nextStart;

    if (currentEnd === null) {
      const defaultEnd = addMinutes(nextStart, emptyEndDurationMinutes);
      void formik.setFieldValue(endField, defaultEnd);
      carryIntoEndDay(differenceInCalendarDays(defaultEnd, nextStart));
      return;
    }

    // A half-typed end is an entry in progress, not an empty field, and without a previous start
    // there is no distance to move by.
    if (!isValidDate(currentEnd) || !isValidDate(previousStart)) {
      return;
    }

    const { shiftedEnd, carriedDays } = shiftEnd(currentEnd, previousStart, nextStart);
    void formik.setFieldValue(endField, shiftedEnd);
    carryIntoEndDay(carriedDays);
  };
}

/**
 * An end pushed past midnight carries into the neighbouring day, so an appointment that spans midnight
 * keeps its exact duration; the carried days are returned for a separate end-day field. The arithmetic
 * is on wall-clock time, so a shift across a DST change keeps the displayed times; only an end landing
 * in the spring-forward gap is pushed an hour later.
 */
function shiftEnd(end: Date, previousStart: Date, nextStart: Date): { shiftedEnd: Date; carriedDays: number } {
  const shiftedMinute = minuteOfDay(end) + minuteOfDay(nextStart) - minuteOfDay(previousStart);
  const carriedDays = Math.floor(shiftedMinute / minutesInDay);
  const minuteOfShiftedDay = shiftedMinute - carriedDays * minutesInDay;

  const shiftedEnd = set(addDays(end, differenceInCalendarDays(nextStart, previousStart) + carriedDays), {
    hours: Math.floor(minuteOfShiftedDay / minutesInHour),
    minutes: minuteOfShiftedDay % minutesInHour,
  });

  return { shiftedEnd, carriedDays };
}

function minuteOfDay(date: Date): number {
  return getHours(date) * minutesInHour + getMinutes(date);
}
